import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { title } from 'process';
import { finalize } from 'rxjs/operators';
import swal from 'sweetalert2';
import { isNullOrUndefined } from 'util';
import { markAsTouchDeep } from '../../shared/models/form-utilities';
import { IsRequiredWithGenericMessage } from '../../shared/models/validators';
import { ProductComponent, ProductComponents } from '../models/component';
import { Products } from '../models/product';

import { ProductService } from '../product.service';

@Component({
  selector: 'app-create-product',
  templateUrl: './create-product.component.html',
  styleUrls: ['./create-product.component.scss']
})
export class CreateProductComponent implements OnInit {

  showloader = false;
  addProductForm: FormGroup;
  isFormSubmitted = false;
  productId: number;
  components: ProductComponents = [];
  productNames: string[] = []
  componentsTouched: boolean = false;

  constructor(private readonly productService: ProductService,
    private route: ActivatedRoute,
    private router: Router, private readonly cdr: ChangeDetectorRef) { }

  ngOnInit() {
    this.addProductForm = new FormBuilder().group({
      product_name: new FormControl("", [IsRequiredWithGenericMessage]),
      product_description: new FormControl("", [IsRequiredWithGenericMessage])
    }, { validator: this.validateForm() });

    this.getAllProducts();
  }

  validateForm(): ValidationErrors {
    return (group: FormGroup): ValidationErrors => {
      const productControl = group.get('product_name');
      const productName: string = productControl.value;
      if (productName && this.productNames && this.productNames.includes(productName.trim().toLowerCase())) {
        this.addError(productControl, 'Duplicate product name')
      }
      return null;
    }
  }

  addError(formControl: AbstractControl, error: string) {
    if (formControl.errors) {
      formControl.errors['Error'] = error;
    } else {
      const validationErrors: ValidationErrors = {};
      validationErrors['Error'] = error;
      formControl.setErrors(validationErrors);
    }
  }

  private getAllProducts(): void {
    this.showloader = true;
    this.productService.getAllProducts()
      .pipe(
        finalize(() => this.showloader = false))
      .subscribe((result: Products) => {
        this.productNames = result.map(product => product.product_name.trim().toLowerCase());
        let ids = result.map(product => product.product_id).sort((a, b) => a - b);
        this.productId = ids[ids.length - 1] + 1; // Increment the last ID
      });
  }

  createProduct() {
    if (this.addProductForm.invalid || !this.componentValid) {
      markAsTouchDeep(this.addProductForm);
      this.componentsTouched = true;
      return;
    }

    this.showloader = true;

    const data = {
      ...this.addProductForm.value,
      product_id: this.productId,
      product_components: this.components.map(c => c.name.trim())
    }

    this.productService.createProduct(data)
      .pipe(finalize(() => this.showloader = false))
      .subscribe({
        next: (result: any) => {
          let title: string = 'Success!';
          let message: string = 'Product created successfully.';
          let type: any = 'success';
          let buttonText: string = 'Ok';
          let nextFunc: () => void = () => {
            this.resetClientForm();
            this.router.navigateByUrl('/loadsheets');
          }

          if (result === false) {
            message = 'Failed to create product';
            type = 'warning';
            nextFunc = () => {
              this.resetClientForm();
            }
          }

          this.showAlert(message, type, buttonText, () => nextFunc());

        },
        error: () => {
          this.showAlert('Failed to create new product', 'warning', 'Ok', () => this.resetClientForm())
        }
      });
  }

  showAlert(message: string, type: any, buttonText: string, callback: () => void): void {
    swal({
      text: message,
      type: type,
      showCancelButton: false,
      confirmButtonColor: 'rgb(17, 205, 239)',
      confirmButtonText: buttonText
    }).then(() => callback());
  }

  resetClientForm() {
    this.addProductForm.reset();
    this.componentsTouched = false;
  }

  get componentValid(): boolean {
    if (isNullOrUndefined(this.components) || this.components.length === 0) {
      // Invalid if there are no components associated with the product.
      return false;
    }

    if (this.components.findIndex(c => !isNullOrUndefined(c.firstError)) > 0) {
      return false
    }

    return true;
  }

  get product_name() {
    return this.addProductForm.get('product_name')
  }

  get product_description() {
    return this.addProductForm.get('product_description')
  }

  public addComponent(): void {
    this.components.push(new ProductComponent({ isEditing: true }));
    this.componentsTouched = true;
  }

  public removeComponent(index: number): void {
    this.components.splice(index, 1);
    this.validateComponents();
  }

  private validateComponents() {
    this.components.forEach(component => component.validate(this.components));
  }

  public removeEditingTemplate(event: KeyboardEvent, component: ProductComponent): void {
    if (event && event.key) {
      switch (event.key) {
        case "Enter": this.navigateComponent(component, null, component.name.trim() !== '');
          break;
        case "Esc":
        case "Escape":
          component.isEditing = false;
          this.validateComponents()
          break;
      }
    }
  }

  public navigateComponents(event: KeyboardEvent, component: ProductComponent): void {
    if (event && event.key === "Tab") {
      this.navigateComponent(component, event);
    }
  }

  private navigateComponent(component: ProductComponent, event?: KeyboardEvent, createNew: boolean = false) {
    const index = this.components.indexOf(component);
    const newIndex = (event && event.shiftKey) ? index - 1 : index + 1;
    component.isEditing = false;

    if (this.components[newIndex]) {
      // Allow the tab key to focus the next input.
      this.components[newIndex].isEditing = true;

      if (!isNullOrUndefined(event)) {
        event.preventDefault();
      }
    } else {
      if (createNew === true) {
        this.addComponent();
      }
    }

    this.validateComponents();
  }

  public setAsEditable(component: ProductComponent): void {
    component.isEditing = true;
  }

  public trackByFn(index): number {
    return index;
  }

  public componentBlur(component: ProductComponent): void {
    component.isEditing = false;

    this.validateComponents();
  }

  getFirstError(control: AbstractControl): string {
    if (isNullOrUndefined(control) || isNullOrUndefined(control.errors)) {
      return null;
    }
    const key = Object.keys(control.errors)[0];
    return control.errors[key];
  }
}
